import React from 'react'

import { FinancialsTableContextProvider } from '../../components/financials-table'
import { useWindowSize } from '../../hooks/use-window-size'
import { useCachedLoaderData } from '../../hooks/useCachedLoaderData'
import { IntersectionObserverContextProvider } from '../../services/IntersectionObserverContext'
import { NoDataPlaceholder } from './NoDataPlaceholder'
import { TrafficDomainSection } from './TrafficDomainSection'
import { COOKIE_KEY, DEFAULT_SETTINGS } from './constants'
import { InitialTrafficData, ParsedTrafficData } from './types'
import { getParsedDataFromInitial, getSortedTickerTrafficsWithPeers } from './utils'

function TrafficComponent() {
  const { clientWidth } = useWindowSize()
  const { data } = useCachedLoaderData<InitialTrafficData | null>()

  const parsedData = React.useMemo<ParsedTrafficData | null>(() => {
    if (!data) return null

    return getParsedDataFromInitial(data)
  }, [data])

  const tickerTrafficsWithPeers = React.useMemo(() => {
    if (!parsedData) return []

    return getSortedTickerTrafficsWithPeers(parsedData)
  }, [parsedData])

  React.useEffect(() => {
    // Remove min-height from react root element which is set on BE because of CLS
    document.getElementById('root')?.style.removeProperty('min-height')
  }, [])

  if (!data || data.appsWithPeers.length === 0) {
    return <NoDataPlaceholder companyName={parsedData?.companyName ?? ''} />
  }

  return (
    <IntersectionObserverContextProvider>
      <div className="is-js-module space-y-12" style={{ maxWidth: Math.max(clientWidth, 1009) }}>
        {tickerTrafficsWithPeers.map((trafficWithPeers, index) => (
          <TrafficDomainSection
            key={trafficWithPeers.domain}
            parsedTrafficData={parsedData!}
            domain={trafficWithPeers.domain!}
            index={index}
          />
        ))}
      </div>
    </IntersectionObserverContextProvider>
  )
}

export default function Traffic() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={COOKIE_KEY}>
      <TrafficComponent />
    </FinancialsTableContextProvider>
  )
}
